import { saveAs } from "file-saver";
import ExcelJS from "exceljs";
function formatDateForOutward(input) {
  if (!input) return "Not provided";

  try {
    const date = new Date(input); // works with ISO strings and timestamps
    if (isNaN(date.getTime())) return input;

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return input;
  }
}
const handleExportExcelOutward = async (filteredData, project) => {
  try {
    if (!filteredData || filteredData.length === 0) {
      console.error("No data available to export");
      return;
    }

    const includeProjectName =
      typeof project === "object" &&
      project !== null &&
      "projectName" in project;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Outward Items");

    const columns = [
      { header: "Request ID", key: "entryId", width: 20 },
      { header: "Site Engineer", key: "Site Engineer", width: 20 },
      { header: "Work Progress", key: "Work Progress", width: 20 },
      { header: "Done By", key: "doneBy", width: 20 },
      { header: "Status", key: "status", width: 15 },
      { header: "Timestamp", key: "timestamp", width: 20 },
      { header: "Item Category", key: "Item Category", width: 20 },
      { header: "Item Description", key: "Item Description", width: 30 },
      { header: "Opening Stock", key: "Opening Stock", width: 20 },
      { header: "Quantity Issued", key: "Quantity Issued", width: 20 },
      { header: "Closing Stock", key: "Closing Stock", width: 20 },
      { header: "Quantity Available", key: "Quantity Available", width: 20 },
      { header: "UOM", key: "UOM", width: 10 },
      { header: "Purpose", key: "Purpose", width: 50 },
      { header: "Item Image", key: "Item Image", width: 30 },
      { header: "Bills Image", key: "Bills Image", width: 30 },
    ];

    columns.unshift({
      header: "Project Name",
      key: "Project Name",
      width: 25,
    });

    worksheet.columns = columns;

    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
    headerRow.alignment = { vertical: "middle", horizontal: "center" };
    headerRow.height = 30;

    assignBackgroundColorToRow(headerRow, "FF4472C4");

    for (const entry of filteredData) {
      const projectName = includeProjectName
        ? project.projectName
        : entry.projectName || "Not provided";

      const baseRowData = {
        entryId: entry.entryId,
        "Site Engineer": entry.siteEngineer || "Not provided",
        "Work Progress": entry.workProgress || "Not provided",
        doneBy:
          entry.requestHistory &&
          Object.values(entry.requestHistory)[0]?.doneBy,
        status:
          entry.requestHistory &&
          Object.values(entry.requestHistory)[0]?.status,
        timestamp:
          entry.requestHistory &&
          formatDateForOutward(
            Number(Object.values(entry.requestHistory)[0]?.timestamp)
          ),
        "Project Name": projectName,
      };

      const billImages = entry.images
        ? Object.values(entry.images).map((img, index) => ({
            text: `Image${index + 1}`,
            hyperlink: img.uri,
          }))
        : [];

      const outwardItems = entry.outwardItems || {};
      const outwardItemKeys = Object.keys(outwardItems);

      if (outwardItemKeys.length === 0) {
        const rowData = { ...baseRowData };
        rowData["Item Category"] = "Not provided";
        rowData["Item Description"] = "Not provided";
        rowData["Opening Stock"] = "0";
        rowData["Quantity Issued"] = "0";
        rowData["Closing Stock"] = "0";
        rowData["Quantity Available"] = "0";
        rowData["UOM"] = "Not provided";
        rowData["Purpose"] = "No Purpose";
        rowData["Item Image"] = "No Image";
        rowData["Bills Image"] =
          billImages.length > 0 ? billImages : "No Image";

        const row = worksheet.addRow(rowData);
        assignBackgroundColorToRow(row, "FFD9E1F2");
        formatCellsWithLinks(row);
      } else {
        outwardItemKeys.forEach((itemKey, index) => {
          const item = outwardItems[itemKey];
          const issueHistories = item.issueHistory
            ? Object.values(item.issueHistory)
            : [];

          if (issueHistories.length > 1) {
            for (let historyIndex in issueHistories) {
              const purpose =
                issueHistories[historyIndex].purpose || "No Purpose";

              const itemImages = issueHistories[historyIndex]?.images
                ? Object.values(issueHistories[historyIndex].images).map(
                    (url, i) => ({
                      text: `Image${i + 1}`,
                      hyperlink: url,
                    })
                  )
                : [];
              const openingStock = item.openingStock || "0";
              const quantityIssued =
                issueHistories[historyIndex].quantity || "0";
              const closingStock =
                parseFloat(openingStock) - parseFloat(quantityIssued);

              const rowData =
                index === 0
                  ? { ...baseRowData }
                  : Object.fromEntries(
                      Object.keys(baseRowData).map((key) => [key, ""])
                    );

              // Update the timestamp field to use the item's date if available
              if (index === 0 && item.date) {
                rowData.timestamp = formatDateForOutward(item.date);
                console.log("Item date:", item.date);
              } else if (issueHistories[historyIndex].timestamp) {
                // Use history timestamp as fallback
                rowData.timestamp = formatDateForOutward(
                  issueHistories[historyIndex].timestamp
                );
              }

              rowData["Item Category"] = item.category || "Not provided";
              rowData["Item Description"] = item.description || "Not provided";
              rowData["Opening Stock"] = openingStock;
              rowData["Quantity Issued"] = quantityIssued;
              rowData["Closing Stock"] = isNaN(closingStock)
                ? "0"
                : closingStock.toString();
              rowData["Quantity Available"] = "NA";
              rowData["UOM"] = item.uom || "Not provided";
              rowData["Purpose"] = purpose;
              rowData["Item Image"] =
                itemImages.length > 0 ? itemImages : "No Image";
              rowData["Bills Image"] =
                index === 0 && billImages.length > 0 ? billImages : "No Image";

              const row = worksheet.addRow(rowData);
              assignBackgroundColorToRow(row, "FFD9E1F2");
              formatCellsWithLinks(row);
            }
          } else {
            if (issueHistories.length === 1) {
              const purpose = issueHistories[0].purpose || "No Purpose";

              const itemImages = issueHistories[0]?.images
                ? Object.values(issueHistories[0].images).map((url, i) => ({
                    text: `Image${i + 1}`,
                    hyperlink: url,
                  }))
                : [];

              const openingStock = item.openingStock || "NA";
              const quantityIssued =
                item.quantityIssued || issueHistories[0].quantity || "0";
              const closingStock =
                parseFloat(openingStock) - parseFloat(quantityIssued);
              const rowData =
                index === 0
                  ? { ...baseRowData }
                  : Object.fromEntries(
                      Object.keys(baseRowData).map((key) => [key, ""])
                    );

              // Update the timestamp field to use the item's date if available
              if (index === 0 && item.date) {
                rowData.timestamp = formatDateForOutward(item.date);
              } else if (issueHistories[0].timestamp) {
                // Use history timestamp as fallback
                rowData.timestamp = formatDateForOutward(
                  issueHistories[0].timestamp
                );
              }

              rowData["Item Category"] = item.category || "Not provided";
              rowData["Item Description"] = item.description || "Not provided";
              rowData["Opening Stock"] = openingStock;
              rowData["Quantity Issued"] = quantityIssued;
              rowData["Closing Stock"] = isNaN(closingStock)
                ? "NA"
                : closingStock.toString();
              rowData["Quantity Available"] = item.quantityAvailable || "0";
              rowData["UOM"] = item.uom || "Not provided";
              rowData["Purpose"] = purpose;
              rowData["Item Image"] =
                itemImages.length > 0 ? itemImages : "No Image";
              rowData["Bills Image"] =
                index === 0 && billImages.length > 0 ? billImages : "No Image";

              const row = worksheet.addRow(rowData);
              assignBackgroundColorToRow(row, "FFD9E1F2");
              formatCellsWithLinks(row);
            }
          }
        });
      }
    }

    function formatCellsWithLinks(row) {
      row.eachCell((cell) => {
        cell.alignment = {
          vertical: "middle",
          horizontal: "left",
          wrapText: true,
        };

        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };

        if (Array.isArray(cell.value)) {
          const formattedLinks = cell.value;
          cell.value = formattedLinks[0];
          cell.font = { color: { argb: "FF0000FF" }, underline: true };

          if (formattedLinks.length > 1) {
            const colNumber = cell.col;
            formattedLinks.slice(1).forEach((link, i) => {
              const newCell = worksheet.getCell(cell.row + i + 1, colNumber);
              newCell.value = link;
              newCell.font = { color: { argb: "FF0000FF" }, underline: true };
              newCell.alignment = {
                vertical: "middle",
                horizontal: "left",
                wrapText: true,
              };
            });
          }
        }
      });
    }

    function assignBackgroundColorToRow(row, color) {
      row.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: color },
        };
      });
    }

    worksheet.columns.forEach((column) => {
      let maxLength = 0;
      column.eachCell({ includeEmpty: true }, (cell) => {
        if (cell.value) {
          const cellValueLength = cell.value.toString().length;
          maxLength = Math.max(maxLength, cellValueLength);
        }
      });
      column.width = Math.max(Math.min(maxLength + 2, 50), 15);
    });

    worksheet.autoFilter = {
      from: { row: 1, column: 1 },
      to: { row: 1, column: columns.length },
    };

    worksheet.views = [
      { state: "frozen", xSplit: 0, ySplit: 1, activeCell: "A2" },
    ];

    const projectName = includeProjectName
      ? project.projectName
      : filteredData[0]?.projectName || "AllSites";

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer]),
      `${projectName}_Outward_${new Date().toISOString().split("T")[0]}.xlsx`
    );

    return true;
  } catch (error) {
    console.error("Error in handleExportExcelOutward:", error);
    throw error;
  }
};

export default handleExportExcelOutward;
